/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
'use client'

import * as React from 'react'

import { cn } from 'app/lib/utils'

/**
 * The one table frame. Every table in the app wears it at the same radius,
 * whether it is a five-row card on a settings page or the virtualized contacts
 * grid. `DataTable` applies the same constant to its own scroll container.
 */
export const TABLE_FRAME = 'rounded-md border border-default bg-surface'

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<'table'> & {
  /** Extra classes for the scroll container that carries the frame. */
  containerClassName?: string
}) {
  return (
    <div data-slot="table-container" className={cn('relative w-full overflow-x-auto', TABLE_FRAME, containerClassName)}>
      <table data-slot="table" className={cn('w-full caption-bottom text-body-xs', className)} {...props} />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-surface-subtle font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b border-default transition-colors hover:bg-surface-subtle has-aria-expanded:bg-surface-subtle data-[state=selected]:bg-surface-subtle data-[state=warning]:bg-warning-minimal',
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  )
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
